import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { OrdersContext } from "context/OrdersContext";
import { UserContext } from "context/UserContext";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BaseEnum from "enums/BaseEnum";
import OrderDetailContent from "components/OrderDetailContent";

const supportedRoles = [
  "ROLE_MANAGER",
  "ROLE_SALES_RESPONSIBLE",
  "ROLE_RESOURCE",
  "ROLE_LOGISTICS_OFFICER",
  "ROLE_ACCOUNTANT",
  "ROLE_CUSTOMER",
];
const defaultRole = "ROLE_MANAGER";

function Orders() {
  const { user, loading: userLoading } = useContext(UserContext);
  const { fetchOrdersData, getOrderById } = useContext(OrdersContext);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const effectiveRoles = useMemo(() => {
    if (!user || !Array.isArray(user.roles)) return [defaultRole];
    const matchedRoles = user.roles.filter((role) => supportedRoles.includes(role));
    return matchedRoles.length ? matchedRoles : [defaultRole];
  }, [user]);

  const getData = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);

    try {
      const data = user.roles?.includes("ROLE_CUSTOMER")
        ? await fetchOrdersData("ROLE_CUSTOMER")
        : await fetchOrdersData(effectiveRoles[0]);

      setRows(data.rows || []);
      setColumns(
        (data.columns || []).map((col) => ({
          ...col,
          flex: col.flex || 1,
          minWidth: col.minWidth || col.width || 120,
        }))
      );
    } catch (error) {
      toast.error("Veri yüklenirken hata oluştu", { position: "top-right" });
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchOrdersData, effectiveRoles]);

  useEffect(() => {
    if (!userLoading && user) {
      getData();
    }
  }, [userLoading, user, getData]);

  const handleRowClick = (params) => {
    setSelectedId(params.id);
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedOrder(null);

    if (user?.roles?.includes("ROLE_CUSTOMER") && params.row.fullData) {
      setSelectedOrder(params.row.fullData);
      setDetailLoading(false);
      return;
    }

    getOrderById(params.id, effectiveRoles[0])
      .then((data) => {
        setSelectedOrder(data);
      })
      .catch(() => {
        toast.error("Sipariş detayı alınamadı", { position: "top-right" });
        setDetailOpen(false);
      })
      .finally(() => setDetailLoading(false));
  };

  if (userLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h6" color="text">
            Yükleniyor...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!userLoading && !user) {
    return <Navigate to="/authentication/sign-in" replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container justifyContent="flex-start" sx={{ width: "100%" }}>
          <Grid item xs={12} md={12} lg={12}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, marginRight: 2 }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Siparişler
                </MDTypography>
              </MDBox>
              <CardContent>
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  loading={isLoading}
                  disableRowSelectionOnClick
                  onRowClick={handleRowClick}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 400 },
                    },
                  }}
                  localeText={{
                    toolbarColumns: "Sütunlar",
                    toolbarFilters: "Filtreler",
                    toolbarExport: "Dışa Aktar",
                    toolbarDensity: "Yoğunluk",
                    toolbarQuickFilterPlaceholder: "Ara...",
                    noRowsLabel: "Gösterilecek sipariş bulunamadı",
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ pb: 0 }}>
            <MDTypography variant="h5" fontWeight="bold">
              {selectedOrder?.requestFormDTO?.customer?.name || "Sipariş Detayı"}
            </MDTypography>
            <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
              {selectedOrder?.requestFormDTO?.customer?.ticariUnvan || "-"} · #{selectedId ?? "-"}
            </MDTypography>
          </DialogTitle>
          <DialogContent dividers>
            {detailLoading && (
              <MDTypography variant="body2" color="text">
                Yükleniyor...
              </MDTypography>
            )}
            {!detailLoading && selectedOrder && (
              <OrderDetailContent
                order={selectedOrder}
                isCustomer={user?.roles?.includes("ROLE_CUSTOMER")}
                isResource={user?.roles?.includes("ROLE_RESOURCE")}
                isLogistics={user?.roles?.includes("ROLE_LOGISTICS_OFFICER")}
                isAccountant={user?.roles?.includes("ROLE_ACCOUNTANT")}
              />
            )}
            {!detailLoading && !selectedOrder && (
              <MDTypography variant="body2" color="text">
                Sipariş bilgisi yüklenemedi.
              </MDTypography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Kapat</Button>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default Orders;
